import React, { useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import styles from './projectShowcaseCard.module.scss';

/** Tags + subhead hidden at this width and below */
const COMPACT_TEXT_MQ = '(max-width: 500px)';
/** CTA below image (stacked column) below this width */
const STACKED_CTA_MQ = '(max-width: 799px)';

function subscribeMedia(query, callback) {
  const mq = window.matchMedia(query);
  mq.addEventListener('change', callback);
  return () => mq.removeEventListener('change', callback);
}

function matchesMedia(query) {
  return window.matchMedia(query).matches;
}

/** SSR / prerender: wide layout until client matches media. */
function getServerMediaFalse() {
  return false;
}

export default function ProjectShowcaseCard({
  tags = [],
  title,
  subhead,
  description,
  imageSrc,
  imageAlt = '',
  to,
  imgClassName = '',
  mediaClassName = '',
  imageSide = 'right',
  ctaLabel = 'View Case Study',
  className = '',
  comingSoon = false,
  /** Optional extra class for the tags `<ul>` (e.g. page-specific max-width) */
  tagBarClassName = '',
  /** Insert a flex line break before this chip index so later tags start on a new row */
  tagRowBreakBeforeIndex = null,
  /** Heading level for the title (default h3 inline; use h1 with titlePosition="above") */
  titleAs: TitleTag = 'h3',
  /**
   * `inline` (default): title in the left column with tags (home featured cards).
   * `above`: full-width title stacked above the tag/media row (portfolio preview pages — Figma 2199:16868).
   */
  titlePosition = 'inline',
}) {
  const reversed = imageSide === 'left';
  const titleId = React.useId();
  const interactive = Boolean(to) && !comingSoon;
  const externalCta =
    typeof to === 'string' && /^https?:\/\//i.test(to);
  const titleAbove = titlePosition === 'above';

  const hideTagsAndSubhead = useSyncExternalStore(
    (cb) => subscribeMedia(COMPACT_TEXT_MQ, cb),
    () => matchesMedia(COMPACT_TEXT_MQ),
    getServerMediaFalse
  );

  const isStackedCtaLayout = useSyncExternalStore(
    (cb) => subscribeMedia(STACKED_CTA_MQ, cb),
    () => matchesMedia(STACKED_CTA_MQ),
    getServerMediaFalse
  );

  const layoutClass = `${styles.cardLayout} ${reversed ? styles.cardLayoutReversed : ''} ${!interactive ? styles.cardLayoutStatic : ''}`.trim();

  const ctaAriaLabel = `${ctaLabel}: ${title}`;

  const textColumnClass = `${styles.textColumn} ${titleAbove ? styles.textColumnTitleAbove : ''}`.trim();

  const ctaNode = interactive ? (
    externalCta ? (
      <a
        href={to}
        className={styles.cta}
        aria-label={ctaAriaLabel}
        target="_blank"
        rel="noopener noreferrer"
      >
        {ctaLabel}
      </a>
    ) : (
      <Link to={to} className={styles.cta} aria-label={ctaAriaLabel}>
        {ctaLabel}
      </Link>
    )
  ) : (
    <span className={styles.cta}>{ctaLabel}</span>
  );

  const showTags = tags.length > 0 && !hideTagsAndSubhead;
  const showSubhead = Boolean(subhead) && !hideTagsAndSubhead;

  const textBlocks = (
    <>
      {showTags ? (
        <ul
          className={[styles.tagBar, tagBarClassName].filter(Boolean).join(' ')}
        >
          {tags.map((label, i) => (
            <React.Fragment key={`${label}-${i}`}>
              {tagRowBreakBeforeIndex === i ? (
                <li className={styles.tagRowBreak} aria-hidden />
              ) : null}
              <li className={styles.chip}>{label}</li>
            </React.Fragment>
          ))}
        </ul>
      ) : null}
      {!titleAbove ? (
        <TitleTag id={titleId} className={styles.title}>
          {title}
        </TitleTag>
      ) : null}
      {showSubhead ? <p className={styles.subhead}>{subhead}</p> : null}
      {description ? <p className={styles.description}>{description}</p> : null}
    </>
  );

  const bodyColumnDesktop = (
    <>
      {textBlocks}
      {ctaNode}
    </>
  );

  const mediaBlock = (
    <div className={`${styles.media} ${mediaClassName}`.trim()}>
      <img
        className={`${styles.image} ${imgClassName}`.trim()}
        src={imageSrc}
        alt={imageAlt}
      />
    </div>
  );

  return (
    <article
      className={`${styles.card} ${titleAbove ? styles.cardWithTitleAbove : ''} ${className}`.trim()}
      aria-labelledby={titleId}
    >
      {titleAbove ? (
        <TitleTag id={titleId} className={styles.titleAbove}>
          {title}
        </TitleTag>
      ) : null}
      <div className={layoutClass}>
        {isStackedCtaLayout ? (
          <>
            <div className={textColumnClass}>{textBlocks}</div>
            {mediaBlock}
            {ctaNode}
          </>
        ) : (
          <>
            <div className={textColumnClass}>{bodyColumnDesktop}</div>
            {mediaBlock}
          </>
        )}
      </div>
    </article>
  );
}
